import React, { useEffect, useState } from 'react';
import { logger } from '../../utils/logger';
import { useFamilyStore } from '../../stores/familyStore';
import { useAuthStore } from '../../stores/authStore';
import { MemberRoleManager } from './MemberRoleManager';
import type { FamilyRole } from '../../utils/accessControl';
import { PrimaryParentOnly } from '../security/PermissionGate';
import { InvitationsList } from './InvitationsList';
import { useApiService } from '../../services/api';
import type { Database } from '../../lib/supabase';
import { useEntitlements } from '../../hooks/useEntitlements';
import { track } from '../../utils/analytics';

type FamilyMember = Database['public']['Tables']['family_members']['Row'] & {
  user_email?: string; // Dodatkowe pole z email użytkownika
};

/**
 * Komponent wyświetlający listę członków rodziny z kontrolą dostępu
 */
export const FamilyMembersList: React.FC = () => {
  const { user } = useAuthStore();
  const { family, members, fetchFamilyMembers, isLoading, error } = useFamilyStore();
  const [refreshInvitationsFlag, setRefreshInvitationsFlag] = useState(0);
  const bumpInvitationsRefresh = () => setRefreshInvitationsFlag(v => v + 1)

  // Funkcja do generowania inicjałów użytkownika
  const getUserInitials = (email?: string) => {
    const userEmail = email || user?.email
    if (!userEmail) return '?'
    
    const nameParts = userEmail.split('@')[0].split('.')
    
    if (nameParts.length >= 2) {
      return (nameParts[0][0] + nameParts[1][0]).toUpperCase()
    }
    
    return userEmail.substring(0, 2).toUpperCase()
  }

  useEffect(() => {
    if (family) {
      fetchFamilyMembers(family.id);
    }
  }, [family]);

  if (!family) {
    return <div className="text-center py-4">Brak aktywnej rodziny</div>;
  }

  if (isLoading) {
    return <div className="text-center py-4">Ładowanie członków rodziny...</div>;
  }

  if (error) {
    return <div className="text-center py-4 text-error">Błąd: {error}</div>;
  }

  // Funkcja pomocnicza do normalizacji roli
  const normalizeRoleValue = (role: string): FamilyRole => {
    // Usuń białe znaki i zamień na małe litery dla bezpieczniejszego porównania
    const cleanRole = String(role).trim().toLowerCase();
    
    if (cleanRole.includes('parent_a')) return 'parent_a';
    if (cleanRole.includes('parent_b')) return 'parent_b';
    if (cleanRole.includes('child_viewer')) return 'child_viewer';
    
    // Domyślnie zwróć child_viewer
    return 'child_viewer';
  };

  // Funkcja pomocnicza do wyświetlania nazwy roli
  const getRoleName = (role: FamilyRole): string => {
    // Znormalizuj rolę
    const normalizedRole = normalizeRoleValue(role);
    
    switch (normalizedRole) {
      case 'parent_a':
        return 'Rodzic / Opiekun (Admin)';
      case 'parent_b':
        return 'Rodzic / Opiekun';
      case 'child_viewer':
        return 'Obserwator';
      default:
        return String(role);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Członkowie rodziny</h2>
        {/* Ukrywamy wskaźnik liczby członków/limitów zgodnie z decyzją produktową */}
      </div>
      
      {members.length === 0 ? (
        <div className="text-center py-4 text-gray-500">
          Brak członków rodziny
        </div>
      ) : (
        <div className="space-y-4">
          {members.map((member) => (
            <div key={member.id} className="border-b pb-3 last:border-0">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium mr-3">
                  {getUserInitials(member.user_email)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-600">
                    {member.user_email || member.user_id}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center">
                    <span className={`inline-block w-2 h-2 rounded-full bg-green-500 mr-2`}></span>
                    <span>{getRoleName(member.role as FamilyRole)}</span>
                  </div>

                  {/* Sekcja zarządzania rolą przeniesiona pod status */}
                  <div className="mt-2 flex justify-end">
                    <div className="w-full sm:w-72">
                      <MemberRoleManager
                        userId={member.user_id}
                        currentRole={member.role as FamilyRole}
                        onRoleUpdated={() => fetchFamilyMembers(family.id)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <PrimaryParentOnly fallback={
        <div className="mt-6 border-t pt-6">
          <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-xl mb-2">🔒</div>
            <div className="font-medium">Tylko administrator może zapraszać nowych członków</div>
            <div className="text-sm mt-1">
              Skontaktuj się z administratorem rodziny, aby dodać nowych członków.
            </div>
          </div>
        </div>
      }>
        <div className="mt-6 border-t pt-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs mr-2 ${
              members.length >= 8 ? 'bg-gray-400' : 'bg-blue-500'
            }`}>+</span>
            Zaproś nowego członka rodziny
            {/* Nie pokazujemy komunikatu o limicie */}
          </h3>
          {/* Zamiast blokera pokaż formularz; limit jest egzekwowany w tle lub przy zapisie */}
          <InviteMemberForm onInvitationsUpdated={bumpInvitationsRefresh} />
        </div>

        {/* Proste objaśnienie ról */}
        <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Role i uprawnienia</h4>
          <ul className="text-sm text-gray-700 space-y-1">
            <li><span className="font-medium">Rodzic / Opiekun (Administrator)</span> — pełna edycja i funkcje administratora</li>
            <li><span className="font-medium">Rodzic / Opiekun</span> — pełna edycja</li>
            <li><span className="font-medium">Obserwator</span> — dostęp do Opieki, Notatek i Kosztów — bez edycji</li>
          </ul>
        </div>
        
        {/* Lista wysłanych zaproszeń */}
        <div className="mt-6">
          <InvitationsList key={refreshInvitationsFlag} />
        </div>
      </PrimaryParentOnly>
    </div>
  );
};

/**
 * Formularz zapraszania nowego członka rodziny
 * Refaktoryzowany do używania nowej architektury API
 */
const InviteMemberForm: React.FC<{ onInvitationsUpdated?: () => void }> = ({ onInvitationsUpdated }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<FamilyRole>('parent_b');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const { user } = useAuthStore();
  const { family, members } = useFamilyStore();
  const { plan: entitlements } = useEntitlements(family?.id);
  // trigger przekazywany z listy rodziny – nie używamy lokalnego setState aby uniknąć TS6133
  const apiService = useApiService();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!family || !email || !user) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      // Sprawdź maksymalny limit członków rodziny (8)
      if (members.length >= 8) {
        throw new Error('Maksymalnie można mieć 8 członków w rodzinie');
      }

      // Sprawdź limit rodziców jeśli próbujemy zaprosić parent_b
      if (role === 'parent_b') {
        // Policz ilu rodziców już jest w rodzinie (parent_a + parent_b)
        const parentCount = members.filter((member: FamilyMember) => 
          member.role === 'parent_a' || member.role === 'parent_b'
        ).length;
        
        if (parentCount >= 2) {
          throw new Error('Maksymalnie można mieć 2 rodziców w rodzinie (parent_a + parent_b)');
        }
        
        logger.debug(`👥 Sprawdzenie limitów: ${parentCount}/2 rodziców w rodzinie`);
      }

      // Sprawdź limit obserwatorów (non-parent roles)
      if (role !== 'parent_b') {
        const observerCount = members.filter((m: FamilyMember) => m.role !== 'parent_a' && m.role !== 'parent_b').length;
        if (observerCount >= entitlements.maxObservers) {
          throw new Error(`Limit obserwatorów w planie został osiągnięty (maks. ${entitlements.maxObservers}).`);
        }
      }

      logger.debug(`👥 Sprawdzenie limitu członków: ${members.length}/8 członków w rodzinie`);
      
      logger.debug('🚀 Tworzenie zaproszenia przez nowe API...');
      
      // Użyj nowej usługi API zamiast bezpośrednich wywołań Supabase
      const result = await apiService.createInvitation({
        email: email.trim(),
        role: role as 'parent_b' | 'caregiver' | 'child_viewer',
        familyId: family.id
      });

      logger.debug('✅ Zaproszenie utworzone:', result);
      track('invite_sent', { role, source: 'settings' });
      
      // Wyświetl komunikat sukcesu
      setSubmitSuccess(`Zaproszenie wysłane do ${email}`);
      setEmail('');
      
      // Wyczyść komunikat po 3 sekundach
      setTimeout(() => setSubmitSuccess(null), 3000);

      // Zasygnalizuj odświeżenie listy zaproszeń poniżej (bez przeładowania całej strony)
      onInvitationsUpdated?.();

    } catch (error: any) {
      console.error('❌ Błąd tworzenia zaproszenia:', error);
      setSubmitError(error.message || 'Nie udało się utworzyć zaproszenia');
      
      // Wyczyść błąd po 5 sekundach
      setTimeout(() => setSubmitError(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="bg-blue-50 p-3 rounded-lg border border-blue-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="email" className="text-sm font-medium text-blue-700 block mb-1">
              Adres email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="adres@email.com"
              className="w-full px-3 py-2 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            />
          </div>

          <div>
            <label htmlFor="role" className="text-sm font-medium text-blue-700 block mb-1">
              Rola
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as FamilyRole)}
              className="w-full px-3 py-2 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <PrimaryParentOnly>
                <option value="parent_b">Rodzic / Opiekun</option>
              </PrimaryParentOnly>
                          <option value="child_viewer">Obserwator</option>
            </select>
          </div>
        </div>

        {/* Komunikaty o błędach i sukcesie */}
        {submitError && (
          <div className="text-sm text-red-600 mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
            ❌ {submitError}
          </div>
        )}
        
        {submitSuccess && (
          <div className="text-sm text-green-600 mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
            ✅ {submitSuccess}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting || !email}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md transition-colors flex items-center"
          >
            {isSubmitting ? (
              <>
                <span className="mr-2">Wysyłanie...</span>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </>
            ) : (
              <>
                <span className="mr-2">Wyślij zaproszenie</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"></path>
                </svg>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Nie pokazujemy już sekcji z linkiem zaproszenia - jest w tabeli zaproszeń */}
    </div>
  );
};